<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import noNoticesImg from '@/assets/no-notices.svg';
import { getNoticeIcon, type NoticeType } from '@/domains/notifications/notice-icons';
import {
  useMarkAllRead,
  useMarkRead,
  useUnreadNotifications,
} from '@/domains/notifications/queries';
import { Path } from '@/platform/routing/targets';
import { Clear } from '@celerdata/icons';
import { ArrowRight, Close, Loading, RefreshRight } from '@element-plus/icons-vue';

import type { FormattedNotifyMsg } from '@/domains/notifications/queries';

// Per-type accent for the leading icon. Single source of truth shared in
// spirit with NotificationCard / NotificationFlashbar, but kept as a local
// map: this is a different surface (sidebar dropdown) with a smaller icon
// and no ring, so coupling the three components to one map would import
// shape that isn't shared.
const TYPE_ICON_COLOR: Record<NoticeType, string> = {
  info: 'var(--cd-info)',
  warning: 'var(--cd-brand-tangerine)',
  success: 'var(--cd-success)',
  error: 'var(--cd-danger)',
};

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const router = useRouter();

const { data: unread, isPending, isError, isFetching, refetch } = useUnreadNotifications();
const noticeList = computed<FormattedNotifyMsg[]>(
  () => unread.value?.notifyList?.notify_msgs ?? [],
);

// Show the dedicated loading panel only when there's no cached data yet.
// Subsequent background refetches keep the list visible to avoid layout
// flicker — vue-query exposes them via `isFetching` instead.
const showLoading = computed(() => isPending.value && !unread.value);
// Same logic for error: surface the error panel only when we have NO data
// to show. With cached data + a failed refetch we'd rather show stale data
// than blow it away.
const showError = computed(() => isError.value && !unread.value);

function onRetry(): void {
  void refetch();
}

const { mutate: markReadMutate } = useMarkRead();
const { mutate: markAllReadMutate, isPending: markingAll } = useMarkAllRead();

// Set of detail_biz_ids with in-flight markRead mutations. useMarkRead's
// isPending is a single boolean across ALL calls, so a Set is the only way
// to spin only the row the user just dismissed AND let multiple dismisses
// run concurrently.
const pendingItemIds = ref<Set<string>>(new Set());

// Today renders as time only (HH:MM:SS); older items include the short month
// and day. en-GB is fixed intentionally so dates look identical for every
// user regardless of OS region.
function formatNoticeDate(ts: number | undefined): string {
  if (!ts) return '';
  const date = new Date(ts);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = todayStart.getTime() + 24 * 60 * 60 * 1000;
  const isToday = ts >= todayStart.getTime() && ts < todayEnd;
  return date.toLocaleString(
    'en-GB',
    isToday
      ? { hour: 'numeric', minute: 'numeric', second: 'numeric' }
      : {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        },
  );
}

function onItemClick(item: FormattedNotifyMsg): void {
  const jump = item.jumpDetail;
  if (!jump?.link) return;
  if (jump.linkType === 'DOCS' || jump.open_in_new_tab) {
    window.open(jump.link);
  } else {
    void router.push(jump.link);
  }
  emit('close');
}

function onCloseItem(item: FormattedNotifyMsg): void {
  const id = item.detail_biz_id;
  if (!id || pendingItemIds.value.has(id)) return;
  const next = new Set(pendingItemIds.value);
  next.add(id);
  pendingItemIds.value = next;
  markReadMutate(item, {
    onSettled: () => {
      const after = new Set(pendingItemIds.value);
      after.delete(id);
      pendingItemIds.value = after;
    },
  });
}

function onMarkAll(): void {
  if (markingAll.value) return;
  markAllReadMutate();
}

function onSeeMore(): void {
  void router.push(Path.Notification);
  emit('close');
}
</script>

<template>
  <div data-testid="notification-popover">
    <!-- Loading state — initial fetch, no cached data yet. -->
    <div
      v-if="showLoading"
      class="text-fg-2 flex flex-col items-center gap-2 py-10 text-sm"
      data-testid="notification-popover-loading"
    >
      <ElIcon class="animate-spin" :size="20"><Loading /></ElIcon>
      <span>{{ t('notification.loading') }}</span>
    </div>

    <!-- Error state — fetch failed with no cached data. Refresh button retries. -->
    <div
      v-else-if="showError"
      class="flex flex-col items-center gap-3 py-8"
      data-testid="notification-popover-error"
    >
      <div class="text-fg-2 text-sm">{{ t('notification.loadFailed') }}</div>
      <ElButton
        :icon="RefreshRight"
        :loading="isFetching"
        size="small"
        data-testid="notification-popover-retry"
        @click="onRetry"
      >
        {{ t('common.refresh') }}
      </ElButton>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!noticeList.length"
      class="flex flex-col items-center py-8"
      data-testid="notification-popover-empty"
    >
      <img :src="noNoticesImg" alt="" aria-hidden="true" class="mb-3 aspect-square w-25" />
      <div class="text-fg-2 mb-2 text-sm">{{ t('notification.noNewNotice') }}</div>
      <button
        type="button"
        class="text-brand-azure hover:text-brand-azure-hover inline-flex cursor-pointer items-center gap-1 text-sm focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        data-testid="notification-popover-see-more-empty"
        @click="onSeeMore"
      >
        {{ t('common.seeMore') }}
        <ElIcon :size="14"><ArrowRight /></ElIcon>
      </button>
    </div>

    <!-- Populated -->
    <div v-else data-testid="notification-popover-list">
      <div class="flex items-center justify-between px-3 py-2.5">
        <div class="flex items-center gap-2">
          <span class="text-fg text-sm font-semibold">{{ t('notification.title') }}</span>
          <ElTooltip
            effect="dark"
            :content="t('notification.markAllReadTip')"
            placement="top"
            :show-arrow="false"
          >
            <ElIcon
              class="text-fg-3 hover:text-fg-2 cursor-pointer focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
              :class="{ 'animate-spin cursor-wait': markingAll }"
              :size="16"
              role="button"
              tabindex="0"
              :aria-label="t('notification.markAllReadTip')"
              data-testid="notification-popover-mark-all"
              @click="onMarkAll"
              @keydown.enter.prevent="onMarkAll"
              @keydown.space.prevent="onMarkAll"
            >
              <component :is="markingAll ? Loading : Clear" />
            </ElIcon>
          </ElTooltip>
        </div>
        <button
          type="button"
          class="text-brand-azure hover:text-brand-azure-hover inline-flex cursor-pointer items-center gap-1 text-sm focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
          data-testid="notification-popover-see-more"
          @click="onSeeMore"
        >
          {{ t('common.seeMore') }}
          <ElIcon :size="14"><ArrowRight /></ElIcon>
        </button>
      </div>

      <ElScrollbar max-height="420px" view-class="divide-line-soft divide-y">
        <div
          v-for="item in noticeList"
          :key="item.detail_biz_id || item.biz_id"
          class="flex gap-2 px-3 py-2.5"
          :class="
            item.jumpDetail
              ? 'hover:bg-surface-strong cursor-pointer focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current'
              : ''
          "
          :tabindex="item.jumpDetail ? 0 : -1"
          :role="item.jumpDetail ? 'button' : undefined"
          data-testid="notification-popover-item"
          @click="onItemClick(item)"
          @keydown.enter.prevent="onItemClick(item)"
          @keydown.space.prevent="onItemClick(item)"
        >
          <ElIcon class="shrink-0" :size="18" :style="{ color: TYPE_ICON_COLOR[item.msg_type] }">
            <component :is="getNoticeIcon(item.msg_type)" />
          </ElIcon>
          <div class="min-w-0 flex-1">
            <div class="flex items-start gap-2">
              <span class="text-fg flex-1 truncate text-sm font-semibold">{{ item.title }}</span>
              <ElIcon
                class="text-fg-3 hover:text-fg-2 shrink-0 cursor-pointer transition-opacity"
                :class="{
                  'animate-spin cursor-wait':
                    item.detail_biz_id && pendingItemIds.has(item.detail_biz_id),
                }"
                :size="14"
                role="button"
                tabindex="0"
                aria-label="dismiss"
                data-testid="notification-popover-item-close"
                @click.stop="onCloseItem(item)"
                @keydown.enter.stop.prevent="onCloseItem(item)"
                @keydown.space.stop.prevent="onCloseItem(item)"
              >
                <component
                  :is="
                    item.detail_biz_id && pendingItemIds.has(item.detail_biz_id) ? Loading : Close
                  "
                />
              </ElIcon>
            </div>
            <div class="mt-0.5 flex items-start gap-2">
              <span class="text-fg-2 flex-1 truncate text-xs">{{ item.content }}</span>
              <span class="text-fg-3 shrink-0 text-xs">{{
                formatNoticeDate(item.create_time)
              }}</span>
            </div>
          </div>
        </div>
      </ElScrollbar>
    </div>
  </div>
</template>
